import {
  VendorViewModel,
  VendorPacketEventViewModel,
  ZipCountyOptionsViewModel,
  ExcludedZipViewModel
  // TripChargeViewModel
} from '@models/Vendor';
import { createReducer } from '@common/redux-utils';
import {
  ClearVendorAction,
  GetVendorDetailsSuccessAction,
  SaveFormDataAction,
  SaveVendorFailureAction,
  SaveVendorStartAction,
  SaveVendorSuccessAction,
  VendorActionType,
  SetVendorLoadingAction,
  GetZipCountyOptionsSuccessAction,
  SetPacketEventsAction,
  GetVendorFilesSuccessAction,
  SaveVendorFilesSuccessAction,
  RemoveVendorFileSuccessAction,
  SetVendorFilesLoadingAction,
  SetZipsSavingAction,
  SaveIncludedZipsSuccessAction,
  GetVendorBrandCertificationsSuccessAction,
  UpdateVendorBrandCertificationsSuccessAction,
  GetEligibleBrandCertificationsSuccessAction,
  GetVendorBrandCertificationsStartAction,
  GetVendorCVIExceptionsStartAction,
  GetVendorCVIExceptionsSuccessAction,
  UpdateVendorCVIExceptionsSuccessAction,
  GetVendorBrandExceptionsSuccessAction,
  UpdateVendorBrandExceptionsSuccessAction,
  GetVendorBrandExceptionsStartAction,
  GetExcludedZipsSuccessAction,
  SaveExcludedZipsSuccessAction,
  GetExclusionZipCountyOptionsSuccessAction,
  ToggleTradeActivationStartAction,
  ToggleTradeActivationSuccessAction,
  SetTripChargesAction
} from './vendor.actions';
import { OptionsViewModel } from '@models/common.models';
import { DocumentViewModel } from '@models/Document';

export interface VendorState {
  readonly error?: string;
  readonly id: number;
  readonly loadingVendor?: boolean;
  readonly zipsSaving?: boolean;
  readonly message?: string;
  readonly saving?: boolean;
  readonly vendorVM: VendorViewModel;
  readonly trades?: OptionsViewModel[];
  readonly zipCountyOptions?: ZipCountyOptionsViewModel[];
  readonly exclusionZipCountyOptions?: ZipCountyOptionsViewModel[];
  readonly includedZips?: string[];
  readonly excludedZips?: ExcludedZipViewModel[];
  readonly packetEvents?: Array<VendorPacketEventViewModel>;
  readonly documents: DocumentViewModel[];
  readonly isLoading: boolean;
  readonly loadingZips: boolean;
  readonly loadingZipsExclusions: boolean;
  readonly vendorCVIExceptions?: OptionsViewModel[];
  readonly vendorBrandExceptions?: OptionsViewModel[];
  readonly vendorBrandCertifications: OptionsViewModel[];
  readonly eligibleBrandCertifications: OptionsViewModel[];
  readonly isLoadingBrandCertifications: boolean;
  readonly isLoadingCVIExceptions: boolean;
  readonly isLoadingBrandExceptions: boolean;
  readonly isTogglingTradeActivation: boolean;
  readonly zipCodes: Array<string>;
  readonly tripCharges: Array<ExcludedZipViewModel>;
  // readonly tripChargeEdits: Array<TripChargeViewModel>;
}

const initialState: VendorState = {
  id: 0,
  vendorVM: {} as VendorViewModel,
  documents: [],
  isLoading: false,
  loadingZips: false,
  loadingZipsExclusions: false,
  isLoadingBrandCertifications: false,
  isLoadingCVIExceptions: false,
  isLoadingBrandExceptions: false,
  isTogglingTradeActivation: false,
  vendorBrandCertifications: [],
  eligibleBrandCertifications: [],
  zipCodes: [],
  tripCharges: []
  // tripChargeEdits: [],
};

function handleClearVendor(state: VendorState, action: ClearVendorAction): VendorState {
  return initialState;
}

function handleGetZipCountyOptionsSuccess(state: VendorState, action: GetZipCountyOptionsSuccessAction): VendorState {
  return {
    ...state,
    zipCountyOptions: action.zipCountyOptions,
    loadingVendor: false
  };
}

function handleGetExclusionZipCountyOptionsSuccess(
  state: VendorState,
  action: GetExclusionZipCountyOptionsSuccessAction
): VendorState {
  return {
    ...state,
    exclusionZipCountyOptions: action.exclusionZipCountyOptions,
    loadingVendor: false
  };
}

function handleGetVendorDetailsSuccess(state: VendorState, action: GetVendorDetailsSuccessAction): VendorState {
  // Cleaning is done because react complains when setting controlled components to null,
  // Did this to convert to undefined
  // If this is a widespread issue we can convert this to a middleware for the fetch method
  let cleanedVendorVM = action.vendorVM;
  Object.keys(cleanedVendorVM).forEach(key => {
    if (cleanedVendorVM[key] == null) {
      cleanedVendorVM[key] = undefined;
    }
  });
  return {
    ...state,
    loadingVendor: false,
    vendorVM: cleanedVendorVM
  };
}

function handleGetVendorFilesSuccess(state: VendorState, action: GetVendorFilesSuccessAction): VendorState {
  if (!action) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - get vendor files done',
    documents: action.documents
  };
}

function handleSaveVendorFilesSuccess(state: VendorState, action: SaveVendorFilesSuccessAction): VendorState {
  if (!action.documents) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - vendor files saved - ' + action.vendorId
  };
}

function handleRemoveVendorFileSuccess(state: VendorState, action: RemoveVendorFileSuccessAction): VendorState {
  if (!action.fileId) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - vendor file removed - ' + action.vendorId
  };
}

function handleSetVendorFilesLoading(state: VendorState, action: SetVendorFilesLoadingAction): VendorState {
  return {
    ...state,
    isLoading: action.isLoading
  };
}

function handleSetZipsSaving(state: VendorState, action: SetZipsSavingAction): VendorState {
  return {
    ...state,
    zipsSaving: action.isSaving
  };
}

function handleSaveZipsSuccess(state: VendorState, action: SaveIncludedZipsSuccessAction): VendorState {
  return {
    ...state,
    zipsSaving: false
  };
}

function handleSaveExcludedZipsSuccess(state: VendorState, action: SaveExcludedZipsSuccessAction): VendorState {
  return {
    ...state,
    zipsSaving: false
  };
}

function handleGetExcludedZipsSuccess(state: VendorState, action: GetExcludedZipsSuccessAction): VendorState {
  return {
    ...state,
    excludedZips: action.excludedZips
  };
}

function handleSaveFormData(state: VendorState, action: SaveFormDataAction): VendorState {
  const newVendorVM = {
    ...state.vendorVM,
    ...action.partialVendorVM
  } as VendorViewModel;
  return {
    ...state,
    vendorVM: newVendorVM
  };
}

function handleSaveVendorFailure(state: VendorState, action: SaveVendorFailureAction): VendorState {
  return {
    ...state,
    error: action.errorMessage,
    saving: false
  };
}

function handleSaveVendorStart(state: VendorState, action: SaveVendorStartAction): VendorState {
  return {
    ...state,
    saving: true
  };
}

function handleSaveVendorSuccess(state: VendorState, action: SaveVendorSuccessAction): VendorState {
  return {
    ...state,
    id: action.createdID,
    saving: false
  };
}

function handleToggleTradeActivationStart(state: VendorState, action: ToggleTradeActivationStartAction): VendorState {
  return {
    ...state,
    isTogglingTradeActivation: true
  };
}

function handleToggleTradeActivationSuccess(
  state: VendorState,
  action: ToggleTradeActivationSuccessAction
): VendorState {
  return {
    ...state,
    isTogglingTradeActivation: false
  };
}

function handleSetVendorLoading(state: VendorState, action: SetVendorLoadingAction): VendorState {
  return {
    ...state,
    loadingVendor: action.isVendorLoading
  };
}

function handleSetPacketEvents(state: VendorState, action: SetPacketEventsAction): VendorState {
  return {
    ...state,
    packetEvents: action.vendorPacketEvents
  };
}

function handleGetVendorCVIExceptionsStart(state: VendorState, action: GetVendorCVIExceptionsStartAction): VendorState {
  return {
    ...state,
    isLoadingCVIExceptions: true
  };
}

function handleGetVendorCVIExceptionsSuccess(
  state: VendorState,
  action: GetVendorCVIExceptionsSuccessAction
): VendorState {
  return {
    ...state,
    vendorCVIExceptions: action.cvis,
    isLoadingCVIExceptions: false
  };
}

function handleUpdateVendorCVIExceptionsSuccess(
  state: VendorState,
  action: UpdateVendorCVIExceptionsSuccessAction
): VendorState {
  return {
    ...state,
    vendorCVIExceptions: action.cvis
  };
}

function handleDeleteVendorCVIExceptionsSuccess(
  state: VendorState,
  action: UpdateVendorCVIExceptionsSuccessAction
): VendorState {
  return {
    ...state,
    vendorCVIExceptions: action.cvis
  };
}

function handleGetVendorBrandExceptionsStart(
  state: VendorState,
  action: GetVendorBrandExceptionsStartAction
): VendorState {
  return {
    ...state,
    isLoadingBrandExceptions: true
  };
}

function handleGetVendorBrandExceptionsSuccess(
  state: VendorState,
  action: GetVendorBrandExceptionsSuccessAction
): VendorState {
  return {
    ...state,
    vendorBrandExceptions: action.brands,
    isLoadingBrandExceptions: false
  };
}

function handleUpdateVendorBrandExceptionsSuccess(
  state: VendorState,
  action: UpdateVendorBrandExceptionsSuccessAction
): VendorState {
  return {
    ...state,
    vendorBrandExceptions: action.brands
  };
}

function handleDeleteVendorBrandExceptionsSuccess(
  state: VendorState,
  action: UpdateVendorBrandExceptionsSuccessAction
): VendorState {
  return {
    ...state,
    vendorBrandExceptions: action.brands
  };
}

function handleGetVendorBrandCertificationsSuccess(
  state: VendorState,
  action: GetVendorBrandCertificationsSuccessAction
): VendorState {
  return {
    ...state,
    vendorBrandCertifications: action.brandCertifications,
    isLoadingBrandCertifications: false
  };
}

function handleUpdateVendorBrandCertificationsSuccess(
  state: VendorState,
  action: UpdateVendorBrandCertificationsSuccessAction
): VendorState {
  return {
    ...state,
    vendorBrandCertifications: action.brandCertifications
  };
}

function handleGetEligibleBrandCertificationsSuccess(
  state: VendorState,
  action: GetEligibleBrandCertificationsSuccessAction
): VendorState {
  return {
    ...state,
    eligibleBrandCertifications: action.brandCertifications as OptionsViewModel[]
  };
}

function handleGetVendorBrandCertificationsStart(
  state: VendorState,
  action: GetVendorBrandCertificationsStartAction
): VendorState {
  return {
    ...state,
    isLoadingBrandCertifications: true
  };
}

function handleSetTripCharges(state: VendorState, action: SetTripChargesAction): VendorState {
  return {
    ...state,
    tripCharges: action.tripCharges
  };
}

export const VendorReducer = createReducer(initialState, {
  [VendorActionType.ClearVendor]: handleClearVendor,

  [VendorActionType.GetVendorDetailsSuccess]: handleGetVendorDetailsSuccess,
  [VendorActionType.GetZipCountyOptionsSuccess]: handleGetZipCountyOptionsSuccess,
  [VendorActionType.GetExclusionZipCountyOptionsSuccess]: handleGetExclusionZipCountyOptionsSuccess,

  [VendorActionType.GetExcludedZipsSuccess]: handleGetExcludedZipsSuccess,

  [VendorActionType.GetVendorFilesSuccess]: handleGetVendorFilesSuccess,
  [VendorActionType.SaveVendorFilesSuccess]: handleSaveVendorFilesSuccess,
  [VendorActionType.RemoveVendorFilesSuccess]: handleRemoveVendorFileSuccess,
  [VendorActionType.SetVendorFilesLoading]: handleSetVendorFilesLoading,
  [VendorActionType.SaveIncludedZipsSuccess]: handleSaveZipsSuccess,
  [VendorActionType.SaveExcludedZipsSuccess]: handleSaveExcludedZipsSuccess,
  [VendorActionType.SetZipsSaving]: handleSetZipsSaving,
  [VendorActionType.SaveFormData]: handleSaveFormData,

  [VendorActionType.SaveVendorFailure]: handleSaveVendorFailure,
  [VendorActionType.SaveVendorStart]: handleSaveVendorStart,
  [VendorActionType.SaveVendorSuccess]: handleSaveVendorSuccess,

  [VendorActionType.SetVendorLoading]: handleSetVendorLoading,

  [VendorActionType.SetPacketEvents]: handleSetPacketEvents,

  [VendorActionType.ToggleTradeActivationSuccess]: handleToggleTradeActivationSuccess,
  [VendorActionType.ToggleTradeActivationStart]: handleToggleTradeActivationStart,

  [VendorActionType.GetVendorBrandCertificationsSuccess]: handleGetVendorBrandCertificationsSuccess,
  [VendorActionType.UpdateVendorBrandCertificationsSuccess]: handleUpdateVendorBrandCertificationsSuccess,
  [VendorActionType.GetEligibleBrandCertificationsSuccess]: handleGetEligibleBrandCertificationsSuccess,
  [VendorActionType.GetVendorBrandCertificationsStart]: handleGetVendorBrandCertificationsStart,

  [VendorActionType.GetVendorCVIExceptionsSuccess]: handleGetVendorCVIExceptionsSuccess,
  [VendorActionType.UpdateVendorCVIExceptionsSuccess]: handleUpdateVendorCVIExceptionsSuccess,
  [VendorActionType.DeleteVendorCVIExceptionsSuccess]: handleDeleteVendorCVIExceptionsSuccess,
  [VendorActionType.GetVendorCVIExceptionsStart]: handleGetVendorCVIExceptionsStart,

  [VendorActionType.GetVendorBrandExceptionsSuccess]: handleGetVendorBrandExceptionsSuccess,
  [VendorActionType.UpdateVendorBrandExceptionsSuccess]: handleUpdateVendorBrandExceptionsSuccess,
  [VendorActionType.DeleteVendorBrandExceptionsSuccess]: handleDeleteVendorBrandExceptionsSuccess,
  [VendorActionType.GetVendorBrandExceptionsStart]: handleGetVendorBrandExceptionsStart,

  [VendorActionType.SetTripCharges]: handleSetTripCharges
});



// WEBPACK FOOTER //
// ./src/vendor/vendor.reducer.ts